import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Bell, Calendar, ChevronDown } from 'lucide-react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Avatar, AvatarFallback } from '../ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../ui/popover';
import { Calendar as CalendarComponent } from '../ui/calendar';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../ui/command';
import { useAuth } from '../../hooks/useAuth';
import { getInitials, formatDate } from '../../lib/utils';

export function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [date, setDate] = useState(new Date());
  const [searchQuery, setSearchQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);

  const searchOptions = [
    { label: 'Search Transactions', value: 'transactions', path: '/transactions' },
    { label: 'Search Payment Links', value: 'payment-links', path: '/payment-links' },
    { label: 'Search Users', value: 'users', path: '/users' },
    { label: 'Search Beneficiaries', value: 'beneficiaries', path: '/beneficiaries' },
    { label: 'Search Payouts', value: 'payouts', path: '/payouts' },
  ];

  const handleSearch = (e) => {
    if (e.key === 'Enter' && searchQuery.trim()) {
      // Default to transactions search
      navigate(`/transactions?search=${encodeURIComponent(searchQuery)}`);
      setSearchOpen(false);
    }
  };

  const handleSearchSelect = (option) => {
    if (searchQuery.trim()) {
      navigate(`${option.path}?search=${encodeURIComponent(searchQuery)}`);
    } else {
      navigate(option.path);
    }
    setSearchOpen(false);
    setSearchQuery('');
  };

  return (
    <header className="top-header" data-testid="header">
      {/* Search */}
      <div className="flex items-center gap-4 flex-1 max-w-md">
        <Popover open={searchOpen} onOpenChange={setSearchOpen}>
          <PopoverTrigger asChild>
            <div className="relative w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                type="search"
                placeholder="Search transactions, users..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => setSearchOpen(true)}
                onKeyDown={handleSearch}
                className="pl-10 bg-slate-50 border-slate-200"
                data-testid="header-search"
              />
            </div>
          </PopoverTrigger>
          <PopoverContent className="w-[400px] p-0" align="start">
            <Command>
              <CommandInput 
                placeholder="Type to search..." 
                value={searchQuery}
                onValueChange={setSearchQuery}
              />
              <CommandList>
                <CommandEmpty>
                  {searchQuery ? `Press Enter to search "${searchQuery}" in Transactions` : 'Type to search...'}
                </CommandEmpty>
                <CommandGroup heading="Search in">
                  {searchOptions.map((option) => (
                    <CommandItem
                      key={option.value}
                      onSelect={() => handleSearchSelect(option)}
                      className="cursor-pointer"
                    >
                      <Search className="mr-2 h-4 w-4" />
                      {option.label}
                      {searchQuery && (
                        <span className="ml-2 text-slate-500">for "{searchQuery}"</span>
                      )}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>

      {/* Right side */}
      <div className="flex items-center gap-4">
        {/* Date Filter */}
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="gap-2" data-testid="date-filter-btn">
              <Calendar className="h-4 w-4" />
              <span className="text-sm">{formatDate(date.toISOString())}</span>
              <ChevronDown className="h-4 w-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="end">
            <CalendarComponent
              mode="single"
              selected={date}
              onSelect={(d) => d && setDate(d)}
              initialFocus
            />
          </PopoverContent>
        </Popover>

        {/* Notifications */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="relative" data-testid="notifications-btn">
              <Bell className="h-5 w-5" />
              <span className="absolute -top-1 -right-1 h-4 w-4 bg-red-500 rounded-full text-[10px] text-white flex items-center justify-center">
                3
              </span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-80">
            <div className="px-4 py-3 border-b">
              <h4 className="font-semibold">Notifications</h4>
            </div>
            <div className="max-h-80 overflow-y-auto">
              <DropdownMenuItem className="flex flex-col items-start gap-1 p-4 cursor-pointer">
                <span className="font-medium text-sm">High-value transaction detected</span>
                <span className="text-xs text-slate-500">Transaction TXN456789 flagged for review</span>
                <span className="text-xs text-slate-400">2 minutes ago</span>
              </DropdownMenuItem>
              <DropdownMenuItem className="flex flex-col items-start gap-1 p-4 cursor-pointer">
                <span className="font-medium text-sm">Settlement completed</span>
                <span className="text-xs text-slate-500">₹2,45,000 settled to vendor accounts</span>
                <span className="text-xs text-slate-400">1 hour ago</span>
              </DropdownMenuItem>
              <DropdownMenuItem className="flex flex-col items-start gap-1 p-4 cursor-pointer">
                <span className="font-medium text-sm">New user registered</span>
                <span className="text-xs text-slate-500">TechCorp Solutions completed KYB</span>
                <span className="text-xs text-slate-400">3 hours ago</span>
              </DropdownMenuItem>
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem className="text-center text-sm text-blue-600 cursor-pointer">
              View all notifications
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* User Profile */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="gap-2" data-testid="user-menu-btn">
              <Avatar className="h-8 w-8">
                <AvatarFallback className="bg-slate-900 text-white text-xs">
                  {getInitials(user?.name || 'Admin')}
                </AvatarFallback>
              </Avatar>
              <div className="hidden md:flex flex-col items-start">
                <span className="text-sm font-medium">{user?.name || 'Admin'}</span>
                <span className="text-xs text-slate-500">{user?.role || 'Administrator'}</span>
              </div>
              <ChevronDown className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <div className="px-4 py-3 border-b">
              <p className="text-sm font-medium">{user?.name}</p>
              <p className="text-xs text-slate-500">{user?.email}</p>
            </div>
            <DropdownMenuItem>Profile Settings</DropdownMenuItem>
            <DropdownMenuItem>Account</DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={logout} className="text-red-600">
              Logout
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
